import { Link, Tooltip, Typography } from '@material-ui/core';
import { Error } from '@material-ui/icons';
import { mdiShieldCheck } from '@mdi/js';
import { FormattedDate } from '@superdispatch/dates';
import { ColorDynamic, Stack, Tag } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { DriverCdlVerificationShieldIcon } from 'shared/icons/DriverCdlVerificationShieldIcon';
import { createSvgIcon } from 'shared/icons/IconUtils';
import styled from 'styled-components';
import { DriverCdlVerificationStatus } from './DriverCdlVerificationStatus';
import { DRIVER_CDL_VERIFICATION_HELP_URL } from './driverCdlVerificationHelpUrl';

interface DriverCdlVerificationBadgeProps {
  status: DriverCdlVerificationStatus | null | undefined;
  verifiedAt?: string | null;
  variant?: 'icon' | 'tag';
}

const VerifiedShieldIcon = createSvgIcon('DriverCdlVerified', mdiShieldCheck);

const STATUS_PRESENTATION = {
  verified: {
    label: 'CDL Verified',
    color: 'green',
    iconColor: ColorDynamic.Green300,
    Icon: VerifiedShieldIcon,
  },
  unverified: {
    label: 'CDL Unverified',
    color: 'yellow',
    iconColor: ColorDynamic.Yellow300,
    Icon: DriverCdlVerificationShieldIcon,
  },
  declined: {
    label: 'CDL Verification Declined',
    color: 'red',
    iconColor: ColorDynamic.Red300,
    Icon: Error,
  },
  expired: {
    label: 'CDL Expired',
    color: 'red',
    iconColor: ColorDynamic.Red300,
    Icon: Error,
  },
} as const;

const StatusIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  vertical-align: middle;

  & svg {
    height: 20px;
    width: 20px;
  }
`;

const StatusTag = styled(Tag)`
  min-width: 0;

  & svg {
    flex-shrink: 0;
    height: 16px;
    width: 16px;
  }
`;

export function DriverCdlVerificationBadge({
  status,
  verifiedAt,
  variant = 'tag',
}: DriverCdlVerificationBadgeProps) {
  const { label, color, iconColor, Icon } =
    STATUS_PRESENTATION[status ?? 'unverified'];
  const tooltip =
    status === 'verified' ? (
      <Stack>
        <Typography>
          The driver's identity was successfully verified using a
          government-issued identification document and facial comparison
          {verifiedAt && (
            <>
              {' '}
              as of <FormattedDate variant="Date" date={verifiedAt} />
            </>
          )}
          .
        </Typography>
        <Link
          href={DRIVER_CDL_VERIFICATION_HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          color="inherit"
        >
          Learn more
        </Link>
      </Stack>
    ) : (
      label
    );

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      arrow={true}
      interactive={true}
      enterTouchDelay={0}
      PopperProps={{
        // Keep the help link in tab order without clipping it to the load card.
        disablePortal: true,
        popperOptions: { positionFixed: true },
      }}
    >
      {variant === 'icon' ? (
        <StatusIcon role="img" aria-label={label} tabIndex={0}>
          <Icon htmlColor={iconColor} aria-hidden={true} />
        </StatusIcon>
      ) : (
        <StatusTag component="span" variant="subtle" color={color} tabIndex={0}>
          <Box as="span" display="inline-flex" marginRight="xxsmall">
            <Icon aria-hidden={true} />
          </Box>
          <Typography component="span" variant="inherit" noWrap={true}>
            {label}
          </Typography>
        </StatusTag>
      )}
    </Tooltip>
  );
}
